import { useLocation } from '@reach/router';
import { graphql, useStaticQuery } from 'gatsby';
import { COUNTRY, locales } from '~globals';

type GatsbySiteData = {
  site: {
    siteMetadata: {
      title: string;
      author: string;
      siteUrl: string;
    };
  };
};

type MetaField = JSX.IntrinsicElements['meta'];

export type BaseHeadProps = {
  title?: string;
  metaFields?: MetaField[];
  structuredData?: string | string[];
};

export const BaseHead: React.FC<BaseHeadProps> = ({ title, metaFields = [], structuredData }): JSX.Element => {
  const { pathname } = useLocation();
  const { site } = useStaticQuery<GatsbySiteData>(graphql`
    query {
      site {
        siteMetadata {
          title
          author
          siteUrl
        }
      }
    }
  `);

  const defaultMetaFields: MetaField[] = [
    { name: 'title', content: title || site.siteMetadata.title },
    { property: 'og:site_name', content: 'Insify' },
    { property: 'og:type', content: 'website' },
    { property: 'og:locale', content: locales[COUNTRY] },
    { property: 'og:url', content: `${site.siteMetadata.siteUrl}${pathname}` },
    { property: 'og:title', content: site.siteMetadata.title },
    { property: 'og:image', content: `${site.siteMetadata.siteUrl}/og-image.jpg` },
  ];

  /** Remove meta fields with empty 'content' attribute and have prop meta fields override default ones if there's a duplicate with the same 'name' or 'property' attribute. */
  const allMetaFields: MetaField[] = defaultMetaFields
    .filter(
      (metaField) =>
        !!metaField.content &&
        !metaFields.find(
          ({ name, property }) => (name && name === metaField.name) || (property && property === metaField.property),
        ),
    )
    .concat(metaFields.filter(({ content }) => !!content));

  const structuredDataArray = typeof structuredData === 'string' ? [structuredData] : structuredData;

  return (
    <>
      <title>{title || allMetaFields.find((metaField) => metaField.name === 'title')?.content}</title>
      {allMetaFields.map((metaField, index) => (
        <meta key={index} {...metaField} />
      ))}
      {structuredDataArray?.map((structuredDataChunk, index) => (
        <script key={index} type='application/ld+json'>
          {structuredDataChunk}
        </script>
      ))}
    </>
  );
};
